import { useId, useMemo, type Ref } from 'react';
import { ArrowLeft } from 'lucide-react';
import type { IngestJobDTO } from '@contracts/ingest';
import { Button } from '../../../components/ui/button';
import { cn } from '../../../lib/utils';
import { useAssistant } from '../AssistantProvider';
import { usePendingBatches } from './api';
import { FileTypeIcon } from './FileTypeIcon';
import { batchTitle, rowTotal } from './labels';

const DAY_MS = 86_400_000;
const MUTED = 'text-[hsl(var(--h-muted-foreground))]';
const LINE = cn('px-3 py-3 text-sm', MUTED);
const fmt = (n: number) => n.toLocaleString('vi-VN');

/** §8.4 "còn X ngày": the whole days left before the batch expires, a part of a day counting as one. */
export function daysLeft(expiresAt: string, now = Date.now()): number {
  return Math.max(1, Math.ceil((Date.parse(expiresAt) - now) / DAY_MS));
}

/** §8.4: the ready batches, newest first (the server's order, kept even when it is not). */
export const newestFirst = (jobs: readonly IngestJobDTO[]): IngestJobDTO[] =>
  [...jobs].sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));

/** What the ⋯ item counts: the ready batches the list holds (≤ 50, `BATCHES_LISTED`). */
export function usePendingCount(): number | null {
  const { data } = usePendingBatches(true);
  return data === undefined ? null : data.length;
}

function mediaTypeOf(job: IngestJobDTO): string {
  if (job.sourceKind === 'text') return 'text/plain';
  return job.documents[0]?.mediaType ?? 'application/octet-stream';
}

function Item({ job, onOpen }: { job: IngestJobDTO; onOpen: (job: IngestJobDTO) => void }) {
  const { prefs } = useAssistant();
  const title = batchTitle(job);
  // Every row the batch read, as the card's "Có N mục" counts them (mockup §4 "198 bản ghi").
  const meta = [`${fmt(rowTotal(job.counts.byKind))} bản ghi`];
  if (job.expiresAt !== null) meta.push(`còn ${daysLeft(job.expiresAt)} ngày`);
  const drifted = job.drifted ?? 0;
  return (
    <li data-pending-batch={job.id}
      className="flex items-center gap-2.5 rounded-[10px] border border-[hsl(var(--h-border))] bg-[hsl(var(--h-card))] px-2.5 py-2">
      <FileTypeIcon mediaType={mediaTypeOf(job)} filename={job.documents[0]?.filename} />
      <div className="min-w-0 flex-1 leading-tight">
        <p className="truncate text-sm font-semibold text-[hsl(var(--h-card-foreground))]" title={title}>{title}</p>
        <p className={cn('mt-0.5 text-xs tabular-nums', MUTED)}>
          {meta.join(' · ')}
          {drifted > 0 && (
            // D113: counted, never named — a record this person no longer sees counts too. Mockup §4: on this line.
            <>
              {' · '}
              <span className="text-[hsl(var(--h-warning-fg))]">
                {`${fmt(drifted)} dòng đã thay đổi từ lúc ${prefs.personaName} đọc`}
              </span>
            </>
          )}
        </p>
      </div>
      <Button size="sm" variant="outline" aria-label={`Mở lô ${title}`} onClick={() => onOpen(job)}
        className="shrink-0 lg:min-h-8">
        Mở
      </Button>
    </li>
  );
}

/**
 * "Lô chờ duyệt" in the panel (spec §8.4, mockup §4 left): the person's ready batches, newest first, each with what it
 * holds, the days it has left and, when records moved since the batch was read, the drift line (D113). "Mở" hands the
 * batch to `onOpen` (the jump to its card, D120); ✕ goes back to the conversation.
 */
export function PendingBatches({ onOpen, onClose, closeRef }: {
  onOpen: (job: IngestJobDTO) => void; onClose: () => void; closeRef?: Ref<HTMLButtonElement>;
}) {
  const headingId = useId();
  const { data, isPending, isError, refetch, isFetching } = usePendingBatches(true);
  const jobs = useMemo(() => (data === undefined ? [] : newestFirst(data)), [data]);
  const heading = data === undefined ? 'Lô chờ duyệt' : `Lô chờ duyệt · ${data.length}`;
  return (
    <section role="region" aria-labelledby={headingId} className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-center gap-2 px-3 pb-1 pt-2">
        {/* N8: a way back, not a second ✕ under the panel's own ✕ (which closes Hana). */}
        <button ref={closeRef} type="button" aria-label="Quay lại hội thoại" title="Quay lại hội thoại" onClick={onClose}
          className="-ml-1 flex size-11 shrink-0 items-center justify-center rounded-lg text-[hsl(var(--h-muted-foreground))] transition-colors hover:bg-[hsl(var(--h-muted))] hover:text-[hsl(var(--h-card-foreground))] lg:size-7">
          <ArrowLeft className="size-4" aria-hidden="true" />
        </button>
        <h3 id={headingId} className="min-w-0 flex-1 truncate text-sm font-bold text-[hsl(var(--h-card-foreground))]">
          {heading}
        </h3>
      </div>
      {isPending ? (
        <div aria-busy="true" className="flex flex-col gap-2 px-3 py-2">
          {[0, 1].map((i) => (
            <span key={i} className="h-[52px] animate-pulse rounded-[10px] bg-[hsl(var(--h-muted))] motion-reduce:animate-none" />
          ))}
        </div>
      ) : isError ? (
        <div className="flex flex-wrap items-center gap-2 px-3 py-3">
          <p role="alert" className="text-sm text-[hsl(var(--h-error-fg))]">Không tải được danh sách lô.</p>
          <Button size="sm" variant="outline" disabled={isFetching} onClick={() => { void refetch(); }}>Thử lại</Button>
        </div>
      ) : jobs.length === 0 ? (
        <p className={LINE}>Không có lô nào chờ duyệt.</p>
      ) : (
        <ul aria-label="Các lô chờ duyệt" className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto px-3 pb-3 pt-1">
          {jobs.map((job) => <Item key={job.id} job={job} onOpen={onOpen} />)}
        </ul>
      )}
    </section>
  );
}
